import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Locations.css";

const europeanCountries = new Set(["BE", "CH", "ES", "FR", "GB", "GR", "HR", "IS", "IT", "MT", "NL", "PL", "SE", "TR", "CZ"]);
const groupOf = (airport) => airport.countryCode === "US" ? "US" : airport.countryCode === "CA" ? "CA" : europeanCountries.has(airport.countryCode) ? "EU" : "LAC";
const placeOf = (airport) => [...new Set([airport.municipality, airport.region, airport.country].filter(Boolean))].join(", ");
const featuresOf = (airports) => ({
  type: "FeatureCollection",
  features: airports.map((airport) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: [airport.lon, airport.lat] },
    properties: { code: airport.code, name: airport.name, place: placeOf(airport) },
  })),
});

let mapLibrePromise;
function loadMapLibre() {
  if (!mapLibrePromise) {
    mapLibrePromise = new Promise((resolve) => {
      if (!document.querySelector("link[data-coverage-maplibre]")) {
        const css = document.createElement("link");
        css.rel = "stylesheet";
        css.href = "https://unpkg.com/maplibre-gl@6.11.2/dist/maplibre-gl.css";
        css.dataset.coverageMaplibre = "";
        document.head.appendChild(css);
      }
      resolve();
    }).then(() => {
      // MapLibre 6 ships as ESM; webpack must leave this browser import alone.
      return import(/* webpackIgnore: true */ "https://unpkg.com/maplibre-gl@6.11.2/dist/maplibre-gl.mjs");
    }).catch((error) => {
      mapLibrePromise = undefined;
      throw error;
    });
  }
  return mapLibrePromise;
}

function Locations() {
  const [airports, setAirports] = useState([]);
  const [dataError, setDataError] = useState(false);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("");
  const [region, setRegion] = useState("");
  const [selectedCode, setSelectedCode] = useState("");
  const [mapReady, setMapReady] = useState(false);
  const [mapMessage, setMapMessage] = useState("Loading interactive map…");
  const [menuOpen, setMenuOpen] = useState(false);
  const mapNode = useRef(null);
  const mapSectionRef = useRef(null);
  const mapRef = useRef(null);
  const mapLibreRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content");
    const canonical = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonical?.getAttribute("href");
    const robots = document.querySelector('meta[name="robots"]');
    const previousRobots = robots?.getAttribute("content");
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const previousOg = [ogTitle, ogDescription, ogUrl].map(item => item?.getAttribute("content"));
    document.title = "Aircraft Cleaning Coverage at 800+ Airports | LiveTakeoff";
    description?.setAttribute("content", "Search more than 800 LiveTakeoff serviceable airports by code, region, or state for aircraft cleaning and detailing coverage.");
    canonical?.setAttribute("href", "https://www.livetakeoff.com/locations");
    const onLiveDomain = ["www.livetakeoff.com", "livetakeoff.com"].includes(window.location.hostname);
    robots?.setAttribute("content", onLiveDomain ? "index,follow" : "noindex,nofollow");
    ogTitle?.setAttribute("content", document.title);
    ogDescription?.setAttribute("content", description?.getAttribute("content") || "");
    ogUrl?.setAttribute("content", "https://www.livetakeoff.com/locations");
    return () => {
      document.title = previousTitle;
      if (previousDescription != null) description?.setAttribute("content", previousDescription);
      if (previousCanonical != null) canonical?.setAttribute("href", previousCanonical);
      if (previousRobots != null) robots?.setAttribute("content", previousRobots);
      [ogTitle, ogDescription, ogUrl].forEach((item, index) => {
        if (previousOg[index] != null) item?.setAttribute("content", previousOg[index]);
      });
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${process.env.PUBLIC_URL}/coverage-airports.json`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Airport data unavailable");
        return response.json();
      })
      .then(setAirports)
      .catch((error) => {
        if (error.name !== "AbortError") {
          setDataError(true);
          setMapMessage("Airport data could not load.");
        }
      });
    return () => controller.abort();
  }, []);

  const regionOptions = useMemo(() => {
    const options = new Map();
    airports.filter((airport) => !group || groupOf(airport) === group).forEach((airport) => {
      if (airport.regionCode && airport.region) {
        options.set(airport.regionCode, `${airport.region}${group === "US" || group === "CA" ? "" : ` · ${airport.country}`}`);
      }
    });
    return [...options].sort((a, b) => a[1].localeCompare(b[1]));
  }, [airports, group]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return airports.filter((airport) => {
      if (group && groupOf(airport) !== group) return false;
      if (region && airport.regionCode !== region) return false;
      return !term || [airport.code, airport.sourceIdent, airport.currentIcao, airport.name, airport.municipality, ...(airport.searchAliases || [])]
        .some((value) => (value || "").toLowerCase().includes(term));
    });
  }, [airports, query, group, region]);

  const selected = airports.find((airport) => airport.code === selectedCode);
  const openPopup = useCallback((airport) => {
    const map = mapRef.current;
    const maplibregl = mapLibreRef.current;
    if (!map || !maplibregl) return;
    popupRef.current?.remove();
    const popup = document.createElement("div");
    popup.className = "map-popup";
    for (const [tag, value] of [["strong", airport.code], ["span", airport.name], ["small", placeOf(airport)]]) {
      const node = document.createElement(tag);
      node.textContent = value;
      popup.appendChild(node);
    }
    popupRef.current = new maplibregl.Popup({ offset: 18, closeOnMove: false, maxWidth: "260px" })
      .setLngLat([airport.lon, airport.lat]).setDOMContent(popup).addTo(map);
  }, []);
  const selectAirport = useCallback((airport, fly = true, scroll = false) => {
    setSelectedCode(airport.code);
    if (fly && mapRef.current) {
      mapRef.current.flyTo({ center: [airport.lon, airport.lat], zoom: Math.max(mapRef.current.getZoom(), 7), padding: 70, essential: true });
    }
    openPopup(airport);
    if (scroll && window.matchMedia?.("(max-width: 1080px)").matches) {
      mapSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [openPopup]);

  useEffect(() => {
    if (!airports.length || !mapNode.current) return undefined;
    let disposed = false;
    let map;
    let mapHasLoaded = false;
    loadMapLibre().then((maplibregl) => {
      if (disposed) return;
      mapLibreRef.current = maplibregl;
      const phone = window.matchMedia("(max-width: 600px)").matches;
      map = new maplibregl.Map({
        container: mapNode.current,
        style: "https://tiles.openfreemap.org/styles/positron",
        center: phone ? [-97, 39] : [-42, 38],
        zoom: phone ? 2.55 : 2,
        attributionControl: true,
      });
      mapRef.current = map;
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
      map.on("load", () => {
        if (disposed) return;
        mapHasLoaded = true;
        map.addSource("airports", { type: "geojson", data: featuresOf(airports) });
        map.addLayer({ id: "airport-halo", type: "circle", source: "airports", paint: {
          "circle-color": "#e22a39", "circle-radius": ["interpolate", ["linear"], ["zoom"], 1, 5, 5, 8, 10, 13],
          "circle-opacity": ["interpolate", ["linear"], ["zoom"], 1, 0.05, 5, 0.11],
        } });
        map.addLayer({ id: "airport-point", type: "circle", source: "airports", paint: {
          "circle-color": "#d92735", "circle-radius": ["interpolate", ["linear"], ["zoom"], 1, 2, 5, 3.8, 10, 6],
          "circle-opacity": 0.9, "circle-stroke-color": "#fff",
          "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 1, 0.5, 5, 0.9, 10, 1.5],
        } });
        map.on("click", "airport-point", (event) => {
          const airport = airports.find((item) => item.code === event.features?.[0]?.properties?.code);
          if (!airport) return;
          selectAirport(airport, false);
        });
        map.on("mouseenter", "airport-point", () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", "airport-point", () => { map.getCanvas().style.cursor = ""; });
        setMapMessage("");
        setMapReady(true);
      });
      map.on("error", () => {
        if (!mapHasLoaded && !disposed) setMapMessage("Map tiles are unavailable; airport search still works.");
      });
    }).catch(() => {
      if (!disposed) setMapMessage("Interactive map could not load; airport search still works.");
    });
    return () => {
      disposed = true;
      popupRef.current?.remove();
      popupRef.current = null;
      map?.remove();
      mapRef.current = null;
    };
  }, [airports, selectAirport]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map?.getSource("airports")) return;
    map.getSource("airports").setData(featuresOf(visible));
    if (visible.length === 1) {
      map.flyTo({ center: [visible[0].lon, visible[0].lat], zoom: 7, essential: true });
    } else if (visible.length > 1 && visible.length < 300) {
      const bounds = new mapLibreRef.current.LngLatBounds();
      visible.forEach((airport) => bounds.extend([airport.lon, airport.lat]));
      map.fitBounds(bounds, { padding: 55, maxZoom: 7, duration: 400 });
    }
  }, [visible, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map?.getLayer("airport-point")) return;
    map.setPaintProperty("airport-point", "circle-radius", ["case", ["==", ["get", "code"], selectedCode], 10, ["interpolate", ["linear"], ["zoom"], 1, 2, 5, 3.8, 10, 6]]);
    map.setPaintProperty("airport-point", "circle-stroke-width", ["case", ["==", ["get", "code"], selectedCode], 3, ["interpolate", ["linear"], ["zoom"], 1, 0.5, 5, 0.9, 10, 1.5]]);
    const airport = airports.find((item) => item.code === selectedCode);
    if (airport && !popupRef.current) openPopup(airport);
  }, [selectedCode, mapReady, airports, openPopup]);

  useEffect(() => {
    if (selectedCode && !visible.some((airport) => airport.code === selectedCode)) { setSelectedCode(""); popupRef.current?.remove(); popupRef.current = null; }
    const term = query.trim().toLowerCase();
    if (visible.length === 1 && term && [visible[0].code, visible[0].sourceIdent, visible[0].currentIcao, ...(visible[0].searchAliases || [])]
      .some((value) => (value || "").toLowerCase() === term)) selectAirport(visible[0]);
  }, [visible, query, selectedCode, selectAirport]);

  const clearFilters = () => {
    setQuery("");
    setGroup("");
    setRegion("");
    document.getElementById("coverage-search")?.focus();
  };

  return (
    <div className="coverage-page">
      <header className="topbar"><div className="wrap">
        <Link className="brand" to="/login" aria-label="LiveTakeoff Home"><img src={`${process.env.PUBLIC_URL}/coverage-brand-mark.png`} alt="" width="54" height="54" /><strong>Live<span>Takeoff</span></strong></Link>
        <nav className={`coverage-nav${menuOpen ? " open" : ""}`} id="coverage-nav" aria-label="Main navigation"><Link to="/platform" onClick={() => setMenuOpen(false)}>Platform</Link><Link to="/services" onClick={() => setMenuOpen(false)}>Services</Link><Link to="/disinsections" onClick={() => setMenuOpen(false)}>Disinsections</Link><Link to="/locations" aria-current="page" onClick={() => setMenuOpen(false)}>Coverage</Link><Link to="/integrations" onClick={() => setMenuOpen(false)}>Integrations</Link><Link to="/insights" onClick={() => setMenuOpen(false)}>Insights</Link><Link to="/vendors/apply" onClick={() => setMenuOpen(false)}>For vendors</Link><Link className="mobile-contact" to="/shared/contact" onClick={() => setMenuOpen(false)}>Contact</Link></nav><div className="coverage-actions"><Link className="coverage-signin" to="/login#sign-in" onClick={() => setMenuOpen(false)}>Sign in ↗</Link><Link className="coverage-contact" to="/shared/contact">Talk to us</Link><button className="coverage-menu" type="button" aria-controls="coverage-nav" aria-expanded={menuOpen} aria-label={menuOpen ? "Close menu" : "Open menu"} onClick={() => setMenuOpen(open => !open)}>{menuOpen ? "✕" : "☰"}</button></div>
      </div></header>
      <main className="wrap">
        <div className="intro"><div><p className="eyebrow">COVERAGE DISCOVERY</p><h1>Find your airport.</h1><p>Search by airport code or browse by region and state. LiveTakeoff serves every listed airport. Contact our team to arrange local access and timing for your request.</p></div><span className="notice">800+ serviceable airports</span></div>
        <div className="workspace">
          <section className="finder" aria-label="Airport finder">
            <label htmlFor="coverage-search">Airport code or name</label>
            <div className="search-wrap"><span aria-hidden="true">⌕</span><input id="coverage-search" type="search" autoComplete="off" placeholder="Try DJT, PBI, or KOPF" value={query} onChange={(event) => setQuery(event.target.value)} /></div>
            <div className="filter-grid">
              <div><label htmlFor="coverage-group">Region</label><select id="coverage-group" value={group} onChange={(event) => { setGroup(event.target.value); setRegion(""); }}><option value="">All regions</option><option value="US">United States</option><option value="CA">Canada</option><option value="EU">Europe</option><option value="LAC">Latin America &amp; Caribbean</option></select></div>
              <div><label htmlFor="coverage-region">State / province</label><select id="coverage-region" value={region} onChange={(event) => setRegion(event.target.value)}><option value="">All states &amp; provinces</option>{regionOptions.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></div>
            </div>
            <div className="results-heading"><strong>{dataError ? "Airport data unavailable" : airports.length ? `${visible.length.toLocaleString()} ${visible.length === 1 ? "airport" : "airports"} listed` : "Loading airports…"}</strong><button type="button" onClick={clearFilters}>Clear filters</button></div>
            <div className="results" aria-live="polite">
              {!visible.length && airports.length > 0 && <p className="no-results">{query.trim() ? `No airport matched “${query.trim()}” in the current catalog view. Clear the filters or ask LiveTakeoff to check this airport.` : "No airports match these filters."}</p>}
              {visible.map((airport) => <button key={airport.code} type="button" className={`result${selectedCode === airport.code ? " active" : ""}`} aria-pressed={selectedCode === airport.code} onClick={() => selectAirport(airport, true, true)}><span className="result-top"><span className="result-code">{airport.code}</span><span className="result-name">{airport.name}</span></span><span className="result-place">{placeOf(airport)}</span></button>)}
            </div>
            {selected && <div className="selected"><span className="selected-eyebrow">SELECTED AIRPORT</span><div className="selected-title"><strong>{selected.code}</strong><span>{selected.name}</span></div><p>{placeOf(selected)}</p>{selected.historyNote && <p>{selected.historyNote} · Current ICAO: {selected.currentIcao}</p>}<div className="status"><span className="status-dot" /><div><strong>Served by LiveTakeoff</strong><small>Local access and timing are confirmed for each request.</small></div></div><Link to="/shared/contact">Ask about this location <span aria-hidden="true">↗</span></Link></div>}
          </section>
          <section className="map-wrap" ref={mapSectionRef} aria-label="Interactive airport map"><div id="map" ref={mapNode} className="coverage-map" role="application" aria-label="Map of catalog airports" /><div className="map-summary"><strong>800+</strong><span>airports in the network</span><small>Each red point marks one listed airport</small></div>{mapMessage && <div className="map-loading">{mapMessage}</div>}<div className="map-foot"><span>Map pins use approximate public coordinates.</span><span>Click a point to see the airport.</span></div></section>
        </div>
        <p className="footnote">Airport codes and names come from the LiveTakeoff catalog. Coordinates and state/province labels use the public-domain <a href="https://ourairports.com/data/" target="_blank" rel="noopener noreferrer">OurAirports data export</a> and may be approximate. Contact our team to arrange service at your selected airport.</p>
      </main>
      <footer className="coverage-footer"><div className="wrap"><span>© 2026 Live Takeoff LLC</span><nav aria-label="Footer navigation"><Link to="/login">Home</Link><Link to="/platform">Platform</Link><Link to="/services">Services</Link><Link to="/disinsections">Disinsections</Link><Link to="/integrations">Integrations</Link><Link to="/insights">Insights</Link><Link to="/vendors/apply">For vendors</Link><Link to="/shared/contact">Contact</Link></nav><nav className="legal-links" aria-label="Legal"><Link to="/terms">Terms</Link><Link to="/vendor-terms">Vendor terms</Link><Link to="/privacy-policy">Privacy</Link><Link to="/sms-terms">SMS terms</Link></nav></div></footer>
    </div>
  );
}

export default Locations;
